'use client'

import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { pickSuggestedLocale, SUGGESTION_COPY } from '@/lib/locale-suggestion'
import { getEquivalentPath } from '@/lib/equivalent-path'
import type { PublicLocale } from '@/lib/locale-paths'
import { isNativeAppRuntime } from '@/lib/native-app-runtime'
import { readCountryCookie } from '@/lib/country-cookie'
import { marketLanguageForCountry } from '@/server/domain/market-language'
import { isLocaleSelectorEnabled } from '@/lib/locale-capabilities'
import { STORAGE_KEYS, getStorageBoolean, setStorageBoolean } from '@/lib/pwa-storage'
import {
  PRODUCT_EVENTS,
  captureProductEvent,
  type LocaleSuggestionAnalyticsProperties,
} from '@/lib/product-analytics'

type SuggestionEvent =
  | typeof PRODUCT_EVENTS.LOCALE_SUGGESTION_SHOWN
  | typeof PRODUCT_EVENTS.LOCALE_SUGGESTION_ACCEPTED
  | typeof PRODUCT_EVENTS.LOCALE_SUGGESTION_DISMISSED

/** Idiomas declarados por el navegador, en orden de preferencia. */
function readBrowserLanguages(): readonly string[] {
  const { languages, language } = navigator
  if (languages?.length) return languages
  return language ? [language] : []
}

function track(event: SuggestionEvent, from: PublicLocale, to: PublicLocale): void {
  const properties: LocaleSuggestionAnalyticsProperties = {
    from_locale: from,
    to_locale: to,
    browser_language: readBrowserLanguages()[0] ?? '',
  }
  captureProductEvent(event, properties)
}

/**
 * Decide si hay algo que ofrecer, ya montado en cliente.
 *
 * Se resuelve en un efecto y no al construir el estado a propósito: en el
 * render de hidratación el servidor no ha pintado nada, y adelantarlo daría un
 * desajuste de hidratación en todas las landings.
 */
function useLocaleSuggestion(currentLocale: PublicLocale, enabled: boolean) {
  const [suggestedLocale, setSuggestedLocale] = useState<PublicLocale | null>(null)

  useEffect(() => {
    // En la app nativa el idioma es una preferencia guardada del usuario, no
    // algo que se deduzca del navegador, y un enlace a `/en` lo sacaría de su
    // sesión. (`isNativeAppRuntime` y no `window.Capacitor`: el shim web
    // también inyecta ese objeto.)
    if (!enabled) return
    if (isNativeAppRuntime()) return
    if (getStorageBoolean(STORAGE_KEYS.DISMISSED_LOCALE_SUGGESTION)) return

    // La cookie del edge (ADR-020/021) contrapesa al navegador: ver
    // `pickSuggestedLocale`. Leerla aquí no vuelve dinámica la página.
    const marketLanguage = marketLanguageForCountry(readCountryCookie())
    const target = pickSuggestedLocale(readBrowserLanguages(), currentLocale, marketLanguage)
    if (!target) return

    setSuggestedLocale(target)
    track(PRODUCT_EVENTS.LOCALE_SUGGESTION_SHOWN, currentLocale, target)
  }, [currentLocale, enabled])

  return { suggestedLocale, clear: () => setSuggestedLocale(null) }
}

/**
 * Aviso de que la página existe en el idioma del visitante.
 *
 * Por qué se sugiere en vez de redirigir, y con qué reglas se elige el idioma:
 * `lib/locale-suggestion.ts` y ADR-023.
 *
 * **Todo ocurre en cliente, y eso es el diseño, no un atajo.** Las landings se
 * prerenderizan (`generateStaticParams` + `dynamicParams = false`); leer
 * `Accept-Language` en servidor las volvería dinámicas y añadiría
 * `Vary: Accept-Language` a todo lo que hoy sirve el CDN. `navigator.languages`
 * dice casi lo mismo, gratis, y los buscadores ni lo ejecutan ni ven el aviso.
 *
 * **Superpuesto, no en flujo.** Aparece tras hidratar: en flujo empujaría el
 * hero hacia abajo justo después del primer render (CLS en las páginas de
 * tráfico SEO). Va `fixed` bajo la nav, sobre el hueco superior del hero; a ras
 * del borde tapaba el logo y el selector de idioma. Abajo no cabe: ahí vive el
 * banner de cookies.
 *
 * **`readable-body`** porque `--font-sans` del sitio es la fuente festiva y este
 * aviso se monta fuera del subárbol de la landing que ya la neutraliza: sin
 * esto, un texto pequeño para adultos salía en Mountains of Christmas (§3 de
 * `DESIGN.md`).
 */
export function LocaleSuggestionBanner({ currentLocale }: { currentLocale: PublicLocale }) {
  const pathname = usePathname()
  const enabled = isLocaleSelectorEnabled(pathname)
  const { suggestedLocale, clear } = useLocaleSuggestion(currentLocale, enabled)

  if (!enabled || !suggestedLocale) return null

  const copy = SUGGESTION_COPY[suggestedLocale]

  /**
   * Zanja el aviso: lo registra, lo recuerda y lo retira. Aceptar y cerrar hacen
   * lo mismo salvo el evento — el visitante ya ha elegido, en los dos casos.
   */
  const settle = (event: SuggestionEvent) => {
    setStorageBoolean(STORAGE_KEYS.DISMISSED_LOCALE_SUGGESTION, true)
    track(event, currentLocale, suggestedLocale)
    clear()
  }

  return (
    <div
      // `lang` en el contenedor: el aviso está escrito en un idioma distinto al
      // del documento, y sin esto un lector de pantalla lo pronunciaría con la
      // voz equivocada — justo a quien menos puede permitírselo. El nombre de la
      // región es genérico, no el mensaje: si no, se anuncia dos veces.
      lang={suggestedLocale}
      role="region"
      aria-label={copy.regionLabel}
      className="readable-body fade-in fixed inset-x-4 top-[calc(4.25rem+env(safe-area-inset-top))] z-50 mx-auto max-w-md rounded-2xl border border-border bg-card/95 px-4 py-3 shadow-lg backdrop-blur-sm"
    >
      <div className="flex items-start gap-3 text-sm">
        <div className="flex-1">
          <p className="text-foreground">{copy.message}</p>
          <a
            href={getEquivalentPath(pathname, suggestedLocale)}
            onClick={() => settle(PRODUCT_EVENTS.LOCALE_SUGGESTION_ACCEPTED)}
            className="mt-1 inline-block font-medium text-accent underline underline-offset-2 hover:text-accent/80"
          >
            {copy.cta}
          </a>
        </div>
        <button
          type="button"
          onClick={() => settle(PRODUCT_EVENTS.LOCALE_SUGGESTION_DISMISSED)}
          aria-label={copy.dismiss}
          className="-mr-1 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
